import { useState } from "react";
import { AlertCircle, CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

const PaymentsTestPanel = () => {
  const { isSuperAdmin, loading: roleLoading } = useUserRole();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (roleLoading) return null;

  if (!isSuperAdmin) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Super admin only</AlertTitle>
        <AlertDescription>
          Live payment verification is restricted to super admins.
        </AlertDescription>
      </Alert>
    );
  }

  const runTestCharge = async () => {
    setRunning(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("create-test-payment");
      if (fnError || !data?.url) {
        const message = data?.error || fnError?.message || "Could not start the test charge.";
        setError(message);
        toast({ title: "Test charge failed", description: message, variant: "destructive" });
        return;
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast({
        title: "Stripe Checkout opened",
        description: "Complete the $1 charge in the new tab, then check Stripe Payments.",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error.";
      setError(message);
      toast({ title: "Test charge failed", description: message, variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display">
          <CreditCard size={18} className="text-primary" />
          Live payment verification
        </CardTitle>
        <CardDescription>
          Runs a hidden $1 one-time charge through the live Stripe account so you can confirm funds
          land in your payout balance. Never shown to patients.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>This is a real charge</AlertTitle>
          <AlertDescription>
            The account is in live mode. $1 will be charged to whichever real card you enter, and it
            will appear in Stripe Payments. Refund it from the Stripe dashboard when you are done.
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not start the charge</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{error}</p>
              <Button size="sm" variant="outline" onClick={runTestCharge} disabled={running}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Button onClick={runTestCharge} disabled={running}>
          {running ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Opening Stripe Checkout…
            </>
          ) : (
            <>
              Run $1 live test charge
              <ExternalLink className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>

        <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground space-y-2">
          <p className="font-medium text-foreground">After paying, verify in Stripe:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Payments shows a $1 charge with status Succeeded.</li>
            <li>Balance shows the amount as pending payout.</li>
            <li>
              Developers → Webhooks shows a 200 response for the{" "}
              <code className="text-xs">checkout.session.completed</code> delivery.
            </li>
          </ul>
          <p>
            These charges carry <code className="text-xs">internal_test: true</code> metadata so they
            can be filtered out of revenue reporting.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentsTestPanel;
